import { useQuery } from '@tanstack/react-query';
import { api, GrGameRewardLimitResponse } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

interface UseGameRewardLimitsReturn {
  limits: GrGameRewardLimitResponse[];
  total: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

/**
 * Per-(game, reward type) daily caps — the RESERVED-SLICE axis.
 *
 * Distinct from `useRewardTypeLimits`, which is keyed by reward type alone and
 * is a SHARED pool every game draws from. Both may apply to one reward;
 * enforcement takes the lower remaining.
 *
 * Pass `gameConfigId` to scope to one game. The key includes it so switching
 * games does not serve the previous game's caps from cache.
 */
export function useGameRewardLimits(gameConfigId?: string): UseGameRewardLimitsReturn {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: queryKeys.gameRewards.gameRewardLimits(gameConfigId),
    queryFn: () => api.listGameRewardsGameLimits({ gameConfigId, limit: 200 }),
  });

  return {
    limits: data?.limits ?? [],
    total: data?.total ?? 0,
    loading: isLoading,
    error: error instanceof Error ? error.message : isError ? 'Failed to load per-game caps' : null,
    refresh: () => {
      refetch();
    },
  };
}
